// src/admin/VideoGalleryManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Plus, Edit, Trash2, X, Upload, Video, Save, AlertCircle, Play
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const VideoGalleryManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const defaultFormData = {
    title: '',
    description: '',
    videoUrl: '',
    thumbnail: '',
    order: 1,
    active: true,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'galleryVideos'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.order || 99) - (b.order || 99));
      setVideos(data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToImageKit(file, 'video-thumbnails');
      setFormData({ ...formData, thumbnail: url });
      setMessage({ type: 'success', text: 'Thumbnail uploaded!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.videoUrl || !formData.thumbnail) {
      setMessage({ type: 'error', text: 'Video URL aur Thumbnail zaroori hai.' });
      return;
    }

    const videoData = {
      title: formData.title || '',
      description: formData.description || '',
      videoUrl: formData.videoUrl,
      thumbnail: formData.thumbnail,
      order: Number(formData.order) || 1,
      active: formData.active === true,
    };

    try {
      if (editingVideo) {
        await updateDoc(doc(db, 'galleryVideos', editingVideo.id), videoData);
        setSuccessMessage('Video updated!');
      } else {
        const newId = Date.now().toString();
        await setDoc(doc(db, 'galleryVideos', newId), {
          ...videoData,
          createdAt: new Date(),
        });
        setSuccessMessage('Video added!');
      }
      setShowModal(false);
      setShowSuccess(true);
      fetchVideos();
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save video.' });
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Delete video "${video.title || 'Untitled'}"?`)) return;
    try {
      await deleteDoc(doc(db, 'galleryVideos', video.id));
      setSuccessMessage('Video deleted!');
      setShowSuccess(true);
      fetchVideos();
    } catch (error) {
      alert('Failed to delete.');
    }
  };

  const openAddModal = () => {
    setEditingVideo(null);
    const nextOrder = videos.length > 0 ? Math.max(...videos.map(v => v.order || 0)) + 1 : 1;
    setFormData({ ...defaultFormData, order: nextOrder });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title || '',
      description: video.description || '',
      videoUrl: video.videoUrl || '',
      thumbnail: video.thumbnail || '',
      order: video.order || 1,
      active: video.active !== false,
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  return (
    <div className="space-y-6">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Video className="text-green-700" size={26} /> Video Gallery
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            YouTube, Vimeo, ya direct video file ka URL add karein.
          </p>
        </div>
        <button onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md">
          <Plus size={18} /> Add Video
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      ) : videos.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Video size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No videos yet. Add your first one!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map(video => (
            <div key={video.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition group">
              <div className="relative aspect-video bg-gray-100">
                <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />

                <span className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-1 rounded-full ${
                  video.active !== false ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                }`}>
                  {video.active !== false ? 'ACTIVE' : 'INACTIVE'}
                </span>

                <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-1 rounded-full bg-black/60 text-white">
                  #{video.order || 1}
                </span>

                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 gap-2">
                  <button onClick={() => openEditModal(video)}
                    className="bg-blue-500 text-white p-2 rounded-full hover:scale-110 transition">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(video)}
                    className="bg-red-500 text-white p-2 rounded-full hover:scale-110 transition">
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Play icon (small, always visible) */}
                <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                  <Play size={14} className="ml-0.5" style={{ color: '#16a34a' }} fill="#16a34a" />
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-sm text-gray-800 line-clamp-1">
                  {video.title || 'Untitled Video'}
                </h3>
                {video.description && (
                  <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{video.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-800">
                {editingVideo ? 'Edit Video' : 'Add Video'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {message.type === 'error' && <AlertCircle size={16} />}
                  {message.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Video URL *</label>
                <input type="url" value={formData.videoUrl}
                  onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="https://www.youtube.com/watch?v=..." required />
                <p className="text-xs text-gray-500 mt-1">
                  YouTube, Vimeo, ya direct MP4 link
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Thumbnail Image *</label>
                {formData.thumbnail ? (
                  <div className="relative">
                    <img src={formData.thumbnail} alt="preview" className="w-full aspect-video object-cover rounded-lg" />
                    <label className="absolute bottom-2 right-2 bg-white text-gray-700 px-3 py-1 rounded-lg shadow-md text-xs font-semibold cursor-pointer hover:bg-gray-50">
                      Change
                      <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" />
                    </label>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
                        <p className="mt-3 text-sm text-gray-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="text-gray-400 mb-2" size={32} />
                        <p className="text-sm font-semibold text-gray-600">Click to upload thumbnail</p>
                        <p className="text-xs text-gray-400 mt-1">Recommended: 1280x720 px</p>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title (Optional)</label>
                <input type="text" value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., How We Farm Organically" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description (Optional)</label>
                <textarea value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                  placeholder="Short description..." />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Display Order</label>
                <input type="number" value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 1 })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  min="1" />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 text-green-700" />
                <span className="text-sm font-semibold text-gray-700">Show on Website</span>
              </label>

              <div className="flex gap-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit" disabled={uploading}
                  className={`flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition flex items-center justify-center gap-2 ${uploading ? 'opacity-70 cursor-not-allowed' : ''}`}>
                  <Save size={18} /> {editingVideo ? 'Update' : 'Create'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default VideoGalleryManagement;